import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Smile
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const BunnySection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  // Overview content
  const overviewContent = {
    intro: {
      en: "The Bunny section is designed for boys and girls aged 3 to 5 years, introducing them to scouting values through playful activities and basic learning experiences in the Tamtola unit.",
      ta: "பன்னி பிரிவு 3 முதல் 5 வயது வரையிலான சிறுவர்களுக்காக வடிவமைக்கப்பட்டுள்ளது, விளையாட்டு நடவடிக்கைகள் மற்றும் அடிப்படை கற்றல் அனுபவங்கள் மூலம் சாரணர் மதிப்புகளை அறிமுகப்படுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "3 to 5 years", ta: "3 முதல் 5 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Tamtola", ta: "தம்டோலா" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Keep Smiling", ta: "புன்னகையுடன் இருங்கள்" },
        icon: Smile
      }
    ],
    objectives: [
      {
        title: { en: "Social Development", ta: "சமூக வளர்ச்சி" },
        content: { 
          en: "Learning to interact with peers and develop basic social skills through group activities",
          ta: "குழு செயல்பாடுகள் மூலம் சக மாணவர்களுடன் தொடர்புகொள்ள கற்றுக்கொள்வது மற்றும் அடிப்படை சமூக திறன்களை வளர்ப்பது"
        }
      },
      {
        title: { en: "Physical Growth", ta: "உடல் வளர்ச்சி" },
        content: {
          en: "Age-appropriate physical activities and games to develop coordination and motor skills",
          ta: "வயதிற்கு ஏற்ற உடல் செயல்பாடுகள் மற்றும் விளையாட்டுகள் மூலம் ஒருங்கிணைப்பு மற்றும் இயக்க திறன்களை வளர்த்தல்"
        }
      },
      {
        title: { en: "Creative Learning", ta: "படைப்பாற்றல் கற்றல்" },
        content: {
          en: "Exploring creativity through stories, songs, and simple craft activities",
          ta: "கதைகள், பாடல்கள் மற்றும் எளிய கைவினை செயல்பாடுகள் மூலம் படைப்பாற்றலை ஆராய்தல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    activities: {
      title: { en: "Key Activities", ta: "முக்கிய செயல்பாடுகள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Registration", ta: "பதிவு" },
      duration: { en: "Initial Stage", ta: "ஆரம்ப நிலை" },
      requirements: [
        { en: "Complete 3 years of age", ta: "3 வயது நிறைவு செய்தல்" },
        { en: "Parent/Guardian consent", ta: "பெற்றோர்/பாதுகாவலர் ஒப்புதல்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Basic Training", ta: "அடிப்படை பயிற்சி" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Learn basic greetings", ta: "அடிப்படை வாழ்த்துக்களை கற்றல்" },
        { en: "Participate in group activities", ta: "குழு செயல்பாடுகளில் பங்கேற்றல்" },
        { en: "Follow simple instructions", ta: "எளிய வழிமுறைகளை பின்பற்றுதல்" }
      ],
      icon: Star
    },
    {
      title: { en: "Regular Activities", ta: "வழக்கமான செயல்பாடுகள்" },
      duration: { en: "Ongoing", ta: "தொடர்ச்சியான" },
      requirements: [
        { en: "Attend weekly meetings", ta: "வார சந்திப்புகளில் கலந்துகொள்ளுதல்" },
        { en: "Participate in games and songs", ta: "விளையாட்டுகள் மற்றும் பாடல்களில் பங்கேற்றல்" },
        { en: "Learn simple craft making", ta: "எளிய கைவினை செய்தல் கற்றல்" }
      ],
      icon: Calendar
    }
  ];

  const keyActivities = [
    {
      category: { en: "Physical Activities", ta: "உடல் செயல்பாடுகள்" },
      activities: [
        { name: { en: "Action Songs", ta: "செயல் பாடல்கள்" }, icon: Heart },
        { name: { en: "Simple Games", ta: "எளிய விளையாட்டுகள்" }, icon: Target },
        { name: { en: "Basic Exercises", ta: "அடிப்படை பயிற்சிகள்" }, icon: Users }
      ]
    },
    {
      category: { en: "Creative Activities", ta: "படைப்பாற்றல் செயல்பாடுகள்" },
      activities: [
        { name: { en: "Story Time", ta: "கதை நேரம்" }, icon: BookOpen },
        { name: { en: "Drawing", ta: "வரைதல்" }, icon: Map },
        { name: { en: "Simple Crafts", ta: "எளிய கைவினை" }, icon: Heart }
      ]
    },
    {
      category: { en: "Social Activities", ta: "சமூக செயல்பாடுகள்" },
      activities: [
        { name: { en: "Group Games", ta: "குழு விளையாட்டுகள்" }, icon: Users },
        { name: { en: "Sharing Time", ta: "பகிர்வு நேரம்" }, icon: Heart },
        { name: { en: "Basic Etiquette", ta: "அடிப்படை நற்பண்புகள்" }, icon: Star }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#FF6B6B] to-[#FFA07A] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "பன்னி பிரிவு" : "Bunny Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FF6B6B] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#FF6B6B] bg-opacity-10 rounded-lg text-[#FF6B6B]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Objectives */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FF6B6B] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#FF6B6B] bg-opacity-10 rounded-lg text-[#FF6B6B]">
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {level.duration[isTamil ? 'ta' : 'en']}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

      {/* Key Activities Section */}
      <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.activities.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FF6B6B] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {keyActivities.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.activities.map((activity, aIndex) => (
                    <div key={aIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#FF6B6B] bg-opacity-10 rounded-lg text-[#FF6B6B]">
                        <activity.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {activity.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BunnySection;